import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from "../../ApiConfig/ApiConfig";
import { FaChevronLeft, FaChevronRight, FaPrint } from 'react-icons/fa';
import Cookies from 'js-cookie';
import { jwtDecode } from "jwt-decode";

const CustomerAgreementDetails = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const navigate = useNavigate();
  const token = Cookies.get('token');
  let customerId = '';

  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      customerId = decodedToken.customerId;
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }
  
  useEffect(() => {
    const fetchAgreements = async () => {
      const apiUrl = `${API_BASE_URL}/useragreement/fetch-agreement?customerId=${customerId}`;
      
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
  
        const data = await response.json();
  
        if (data.data && Array.isArray(data.data)) {
          setAgreements(data.data);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (error) {
        console.error('Error fetching agreements:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    if (customerId) {
      fetchAgreements();
    }
  }, [token, customerId]);

  // Pagination logic
  const totalPages = Math.ceil(agreements.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = agreements.slice(indexOfFirstItem, indexOfLastItem);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrintDetails = (item) => {
    navigate('/customer_dashboard/customer-agreement-details-print', {
      state: { 
        agreement: {
          ...item.agreement,
          ...item.user,
          customerId: item.user?.customerId
        } 
      }
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="p-4 bg-red-100 border-l-4 border-red-500 text-red-700">
      <p>Error: {error}</p>
    </div>
  );

  return (
    <div className="min-w-full mx-auto bg-white rounded-2xl shadow-md overflow-hidden md:mt-20">
      {/* Header */}
      <div className="mb-2 pl-4 text-left">
        <h1 className="text-3xl font-bold text-gray-800 mb-1">
          My Agreements
        </h1>
        <p className="text-gray-600">View and print your agreement details</p>
      </div>
    
      {/* Agreement List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {currentItems.length > 0 ? (
          <>
            {/* Table Header */}
            <div className="grid grid-cols-12 bg-gray-100 p-4 font-semibold text-gray-700">
              <div className="col-span-1"></div>
              <div className="col-span-4">Customer ID</div>
              <div className="col-span-5">Customer Name</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            
            {/* Table Rows */}
            {currentItems.map((item, index) => (
              <div 
                key={index} 
                className="grid grid-cols-12 p-4 border-b border-gray-200 hover:bg-blue-50 transition-colors"
              >
                <div className="col-span-1 flex items-center">
                  <div className="w-8 h-8 bg-[#1C50A3] text-white rounded-full flex items-center justify-center font-bold">
                    {item.user?.name?.charAt(0).toUpperCase() || 'N'}
                  </div>
                </div>
                
                <div className="col-span-4 flex items-center font-medium text-gray-800">
                  {item.user?.customerId || 'N/A'}
                </div>
                
                <div className="col-span-5 flex items-center">
                  {item.user?.name || 'N/A'}
                </div>
                
                <div className="col-span-2 flex justify-end">
                  <button
                    onClick={() => handlePrintDetails(item)}
                    className="flex items-center space-x-2 bg-[#1C50A3] hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <FaPrint />
                    <span>Print</span>
                  </button>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <p>No agreements found</p>
          </div>
        )}
      </div>
    
    </div>
  );
};

export default CustomerAgreementDetails;